// Central place for cc-os Supabase project coordinates used by the portal.
//
// What lives here:
//   - SUPABASE_URL — base URL of the Supabase project this build talks to.
//     Comes EXCLUSIVELY from VITE_SUPABASE_URL; there is deliberately no
//     fallback. The old "option C" fallback baked the PRODUCTION project ref
//     in here so a missing .env.local silently pointed local dev at prod —
//     reversed 2026-07-29 as part of the dev-environment work: a missing env
//     now fails fast at module load (same posture as ./supabase.ts) instead
//     of quietly reaching production.
//   - MCP_FUNCTIONS_URL — `${SUPABASE_URL}/functions/v1`. The Edge Functions
//     base URL — sibling EFs (`cc-os-mcp`, `invite-user`,
//     `delivery-disputed-notify`, etc) all live under this prefix. Pages that
//     need a specific EF should build it as `${MCP_FUNCTIONS_URL}/<verb>`
//     instead of stripping/regexing the MCP URL.
//
// What does NOT belong here:
//   - A hardcoded project ref. Which environment a build targets is decided
//     by env vars only: .env.local / .env.development for dev, GitHub
//     Actions secrets for deploys (see portal-deploy.yml).
//   - Service-role keys or any other secret — service-role belongs server-
//     side only (Edge Functions, Python CLIs).
//   - Publishable / anon keys — those already live in `./supabase.ts` next to
//     the supabase-js client. Don't duplicate.
//   - R2 / Cloudflare credentials, Sentry DSN, etc — they have their own
//     entry points. Keep this file focused on Supabase project coordinates.

const envUrl = import.meta.env.VITE_SUPABASE_URL;

if (!envUrl) {
  throw new Error(
    "VITE_SUPABASE_URL is not set. Copy .env.example to .env.local and point " +
      "it at your local stack (http://127.0.0.1:54421) or the dev project — " +
      "never production.",
  );
}

/** Base URL of the Supabase project this build targets (from env, required). */
export const SUPABASE_URL: string = envUrl;

/**
 * Edge Functions base URL — `${SUPABASE_URL}/functions/v1`. All cc-os EFs
 * live under this prefix; build per-verb URLs as
 * `${MCP_FUNCTIONS_URL}/<verb>` rather than concatenating ad hoc.
 */
export const MCP_FUNCTIONS_URL = `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1`;
